import QueryString from "qs";
import { io, Socket } from "socket.io-client";
import type { ISocketLoader } from "~/types/socketLoader";

const socket = ref<Socket>();

const socketLoaders = ref<Array<ISocketLoader>>([]);

const subscribedChannels = ref<Set<string>>(new Set());

export default () => {
  const { accountId, userId, contextId } = storeToRefs(useWorkspacesStore());

  const getSocketClient = () => {
    if (!socket.value) {
      const { getAuthCookie } = useAuth();
      //ws://localhost:3000 for local testing
      //wss://coolsocket.homerunner.services for production
      socket.value = io("wss://coolsocket.homerunner.services", {
        transports: ["websocket"],
        secure: true,
        rejectUnauthorized: false,
        auth: {
          accessToken: getAuthCookie().value,
          authContext: contextId.value,
          loginType: "user",
        },
      });

      socket.value.on("connect", () => {
        // Re-subscribe to all tracked channels on connect/reconnect
        subscribedChannels.value.forEach((channel) => {
          socket.value!.emit("join-channel", channel);
          //console.log("joined channel");
        });
      });
      socket.value.onAny((eventName, ...args) => {
        //console.log(`Socket event received: ${eventName}`, args);
      });
    }

    return socket.value;
  };

  const hasSocketClient = computed(() => !!socket.value);

  const destroySocketClient = () => {
    if (!hasSocketClient.value) return;
    socket.value!.removeAllListeners();
    socket.value!.disconnect();
    socket.value = undefined;
    subscribedChannels.value.clear();
  };

  const unbindSocketListeners = () => {
    if (!hasSocketClient.value) return;
    socket.value!.removeAllListeners();
  };

  const setupBaseListeners = () => {
    bindListener(
      `private-consignment.finalize.${accountId.value}.user.${userId.value}`,
      "consignment.finalize.state.change",
      (event) => {
        handleSocketEvent(event);
      }
    );
    bindListener(`private-order_import.${accountId.value}`, "import.state.change", (event) => {
      handleSocketEvent(event);
    });
  };

  const destroyBaseListeners = () => {
    if (!hasSocketClient.value) return;
    const client = getSocketClient();
    importerStates.forEach((key) => {
      client.off(key);
    });
    client.off("state.change");

    destroySocketClient();
  };

  const subscribeToChannel = (channel: string) => {
    const client = getSocketClient();
    subscribedChannels.value.add(channel);
    client.emit("join-channel", channel);
  };

  const unsubscribeFromChannel = (channel: string) => {
    const client = getSocketClient();
    subscribedChannels.value.delete(channel);
    client.emit("leave-channel", channel);
  };

  const bindListener = (channel: string, event: string | null, callback: (e: any) => void) => {
    const client = getSocketClient();
    subscribeToChannel(channel);
    client.on(`${event}`, callback);
  };

  const unbindListener = (channel: string, event: string) => {
    const client = getSocketClient();
    unsubscribeFromChannel(channel);
    client.off(`${event}`);
  };

  const addSocketLoader = (loader: ISocketLoader) => {
    socketLoaders.value.push(loader);
  };

  const handleSocketEvent = (event: any) => {
    if (socketLoaders.value.length) {
      const socketloader = socketLoaders.value.find((val) => val.id == event.id);
      if (!socketloader) return;
      if (event.response.error) {
        socketloader.state = "error";
        socketloader.errorMessage = event.response.error || "An error occurred";
      }
      if (event.response.success) {
        socketloader.state = "success";
        if (event.response.title) {
          socketloader.title = event.response.title;
        }
      }
      if (event.response.status) {
        socketloader.loadingText = event.response.status;
      }
    }
  };

  return {
    getSocketClient,
    hasSocketClient,
    socketLoaders,
    subscribedChannels,
    destroySocketClient,
    setupBaseListeners,
    destroyBaseListeners,
    unbindSocketListeners,
    bindListener,
    unbindListener,
    addSocketLoader,
    handleSocketEvent,
    subscribeToChannel,
    unsubscribeFromChannel,
  };
};
